import { FilterCard, CustomDataTable, useApi, Toaster, CustomAddTagModal, CustomBackdrop, validationMessages } from '../../utils/globalImports';
import { React, useState, useEffect } from '../../utils/globalImports';
import { ApiResponse } from '../../services/apiService';
import { useConfirmCloseDialog } from '../../contexts/ConfirmCloseDialogContext';

interface Tag {
    proposalTagId?: number;
    proposalTag: string;
    status: boolean;
    description: string;
    createdId?: number;
    updatedId?: number;
    createdAt?: Date;
    updatedAt?: Date;
}

const Tags: React.FC = () => {

    // State to manage the modal visibility
    const [modalOpen, setModalOpen] = useState(false);
    const [tagDatalist, setTagDatalist] = useState<Tag[]>([]);
    const [selectedTag, setSelectedTag] = useState<any | null>(null);
    const [tagData, setTagData] = useState<any[]>([]);
    const [filteredData, setFilteredData] = useState<any[]>([]);
    const [currentStatus, setCurrentStatus] = useState<string[]>(['Active']);
    const [toast, setToast] = useState({ open: false, message: "", type: "success" });
    const [isOn, setIsOn] = useState<boolean>();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);

    const { execute, loading } = useApi();
    const { showConfirmCloseDialog } = useConfirmCloseDialog();
    const props = { showClientDropdown: false, title: false, actionText: 'Add Tag', searchbarText: 'Search for a tag by name, description' };

    const userId = Number(localStorage.getItem("userId"));
    
    useEffect(() => {
        fetchTags();
    }, []);  // The empty dependency array ensures it only runs once when the component mounts

    // Fetch tags
    const fetchTags = async () => {
        const { data } = await execute<Tag[]>('get', '/Lookup/GetAllProposalTags');
    
        if (data) {
            setTagDatalist(data);
    
            const tagData = data.map((tag) => {
                const { proposalTagId, createdAt, proposalTag, description, status } = tag; 
                const Status = status ? 'Active' : 'Inactive';
                return { 
                    id: proposalTagId, 
                    createdAt,
                    proposalTagId,  
                    Name: proposalTag,  
                    description: description,  
                    Status 
                };
            });
    
            setTagData(tagData);
    
            const activeTags = tagData
                .filter((tag) => currentStatus.includes(tag.Status))
                .sort((a, b) => a.Name.localeCompare(b.Name, undefined, { sensitivity: 'base' }));
    
            setFilteredData(activeTags);
        }
    };

    // Filter Data
    const handleSearchGridData = (query: string, status: Number) => {
        const numericStatus = Number(status); // Convert status to a number
        const lowerCaseQuery = query.toLowerCase();
    
        // Map statuses to numeric values for filtering
        const statusMapping: Record<string, number> = {
            Active: 1,
            Inactive: 0,
        };
    
        // Ensure that tagData is properly filtered
        const filtered = tagData.filter((item) => {
            const itemStatus = statusMapping[item.Status] ?? -1; // Ensure mapping exists
            const matchesQuery =
                item.Name.toLowerCase().includes(lowerCaseQuery) || //Search in tagName
                (item.description && item.description.toLowerCase().includes(lowerCaseQuery)); //Search in description
    
            const matchesStatus =
                numericStatus === -1 || // If -1 (show all)
                itemStatus === numericStatus; // Match the exact status
    
            return matchesQuery && matchesStatus;
        }).sort((a, b) =>
            a.Name.localeCompare(b.Name, undefined, { sensitivity: 'base' })
        );
    
        //Update filteredData with correctly filtered tags
        setFilteredData(filtered);
    
        //Ensure currentStatus is correctly updated
        setCurrentStatus(
            numericStatus === 0
                ? ['Inactive']
                : numericStatus === -1
                    ? ['Active', 'Inactive']
                    : numericStatus === 1
                        ? ['Active']
                        : currentStatus
        );
    };      

    const handleFormSubmit = async (formData: any, setBackendErrors?: (errors: any) => void) => {
        let response: ApiResponse<any> = { data: null, error: null };
        if (selectedTag) {
            const updatedTag = tagDatalist.find((tag) => tag.proposalTagId === selectedTag.proposalTagId);
            // If the tag is found, update the fields
            if (updatedTag) {
                // Step 2: Update the fields on the filtered record
                const updatedTagData = {
                    ...updatedTag, // Keep the original tag data
                    proposalTag: formData.tagName,
                    description: formData.description,
                    status: formData.status === 'Active' ? true : false, // Update based on Status
                    updatedId: userId,
                };

                response = await execute('put', '/Lookup/UpdateProposalTags', updatedTagData);
            }
        } else {

            // Adding a new tag
            const tag: Tag = {
                proposalTag: formData.tagName,
                description: formData.description,
                status: formData.status === 'Active' ? true : false,
                createdId: userId,
            };

            response = await execute('post', '/Lookup/CreateProposalTag', tag);
        }

        if (response && response.error) {
            if (
              typeof response.error === 'string' &&
              response.error.includes('Tag name already exists')
            ) {
              setBackendErrors?.({ name: response.error });
              return false; // Don't close modal
            } else {
              setToast({ open: true, message: response.error, type: 'error' });
              return false; // Don't close modal on other errors
            }
          } else {
            // Submission successful – close the modal
            setToast({ open: true, message: response?.data, type: 'success' });
            setModalOpen(false);
            setSelectedTag(null);
            handleContentChange(false);
            fetchTags();
            return true;
          }
    };      

    const handleEdit = (tag: any) => {
        setSelectedTag({
            ...tag,
            tagName: tag.Name,
            status: tag.Status === 'Active' ? 'Active' : 'Inactive'
            }); 
        handleOpenModal(); // Open the modal
    };

    const handleAdd = () => {
        setSelectedTag(null);
        setModalOpen(true);
    };

    const handleOpenModal = () => {
        setModalOpen(true);
    };

    const handleToggleState = (row: any) => {
        if (row.Status === 'Inactive') {
            setIsOn(false)
        } else {
            setIsOn(true)
        }
        setSelectedTag(row);
        setDialogOpen(true);
    };

    const handleDialogCancel = () => {
        setDialogOpen(false);
        setSelectedTag(null); 
    };

    const handleDialogConfirm = async () => {
        if (!selectedTag) return;
        try {
            // Find the selected tag in the list
            const updatedTag = tagDatalist.find(
                (tag) => tag.proposalTagId === selectedTag.proposalTagId
            );
    
            if (updatedTag) {
                //Toggle the status dynamically
                const newStatus = updatedTag.status ? false : true;
    
                const updatedTagData = {
                    ...updatedTag,
                    status: newStatus, //Toggle between true (Active) and false (Inactive)
                    updatedId: userId,
                };
    
                //Make the API call to update the status
                const response = await execute('put', '/Lookup/UpdateProposalTags', updatedTagData);
                if (response?.error) {
                    setToast({ open: true, message: response.error, type: 'error' });
                  } else {
                    setToast({ open: true, message: 'Status updated successfully', type: 'success' });
                  }
            }
    
            //Refresh the tag list
            fetchTags();
            setSelectedTag(null);
        } catch (error) {
            console.error('Error updating status:', error);
        } finally {
            setDialogOpen(false);
        }
    };  
    
    const handleContentChange = (state:boolean) => {
    setHasUnsavedChanges(state); 
    };

  const handleClose=()=>{
    if (hasUnsavedChanges) { 
        showConfirmCloseDialog({
          onConfirm: () => { 
            setHasUnsavedChanges(false);  
            setModalOpen(false);       
          },
          onCancel: () => { 
          },
        });
      }
      else{
        setModalOpen(false);
      } 
  }

    return (
        <>
            <FilterCard {...props} handleModalOrNavigate={handleAdd} handleSearch={handleSearchGridData} />
            <CustomAddTagModal handleContentChange={handleContentChange} open={modalOpen} onClose={handleClose} onSubmit={handleFormSubmit} tagData={selectedTag} />
            {tagDatalist.length > 0 && (
                <CustomDataTable
                    rows={filteredData}
                    actionText="Tags"
                    sortOptions={[{ value: "Name", text: "Name" }]}
                    onRowEdit={handleEdit}
                    onToggleClick={handleToggleState}
                    isDialogOpen={dialogOpen}
                    isToggleOn={isOn}
                    handleCancel={handleDialogCancel}
                    handleConfirm={handleDialogConfirm}
                    // gridHeight={'calc(100vh - 500px)'}
                    noResultMessage={validationMessages.common_RecordNotFound("tags")}
                />)}
            <CustomBackdrop open={loading} />
            <Toaster open={toast.open} message={toast.message || ""} type={toast.type as "success" | "error"} onClose={() => setToast({ ...toast, open: false })} />
        </>
    );
};

export default Tags;